import React from "react";
import { getProcessedTextPreview } from "../../../utilities/NewsUtilities";
import { formatDisplayDate } from "../../../utilities/CommonUtilities";

const NewsItem = ({ news, onClick, isDark }) => {
  const theme = isDark
    ? {
        bg: "#0F1424",
        bgSubtle: "#11192C",
        border: "#232B4A",
        text: "#E8ECF8",
        muted: "#A6B0CF",
        link: "#8AB4FF",
        hoverBg: "#121A2E",
        hoverBorder: "#2E5B9A",
      }
    : {
        bg: "#FFFFFF",
        bgSubtle: "#F8FAFC",
        border: "#E5EAF1",
        text: "#0B1220",
        muted: "#5B6472",
        link: "#0D6EFD",
        hoverBg: "#F8FBFF",
        hoverBorder: "rgba(5, 108, 194, 0.22)",
      };

  const displayDate = formatDisplayDate(new Date(news.data_timestamp));
  const title = news.document_name
    ? news.document_name
    : getProcessedTextPreview(news.processed_text, 100);
  const previewText =
    news.summary || getProcessedTextPreview(news.processed_text, 180);

  const relevanceIndicator = (() => {
    const score = news.ner_relevance_score;
    if (score > 0.5) {
      return {
        color: isDark ? "#B9F6C9" : "#166534",
        bg: isDark ? "rgba(46, 204, 113, 0.14)" : "#E8F7EE",
        border: isDark ? "rgba(46, 204, 113, 0.28)" : "#C7EBD3",
        label: "High relevance",
      };
    } else if (score > 0) {
      return {
        color: isDark ? "#FFE39A" : "#9A6700",
        bg: isDark ? "rgba(255, 193, 7, 0.14)" : "#FFF7DB",
        border: isDark ? "rgba(255, 193, 7, 0.26)" : "#F2E0A5",
        label: "Moderate relevance",
      };
    } else {
      return {
        color: isDark ? "#FFC2BC" : "#991B1B",
        bg: isDark ? "rgba(231, 76, 60, 0.14)" : "#FDECEC",
        border: isDark ? "rgba(231, 76, 60, 0.26)" : "#F4CACA",
        label: "Low relevance",
      };
    }
  })();

  const sentimentIndicator = (() => {
    const score = news.sentiment_score;
    if (score >= 0.33) {
      return {
        color: isDark ? "#B9F6C9" : "#166534",
        bg: isDark ? "rgba(46, 204, 113, 0.14)" : "#E8F7EE",
        border: isDark ? "rgba(46, 204, 113, 0.28)" : "#C7EBD3",
        label: "Positive",
      };
    } else if (score > -0.33) {
      return {
        color: isDark ? "#FFE39A" : "#9A6700",
        bg: isDark ? "rgba(255, 193, 7, 0.14)" : "#FFF7DB",
        border: isDark ? "rgba(255, 193, 7, 0.26)" : "#F2E0A5",
        label: "Neutral",
      };
    } else {
      return {
        color: isDark ? "#FFC2BC" : "#991B1B",
        bg: isDark ? "rgba(231, 76, 60, 0.14)" : "#FDECEC",
        border: isDark ? "rgba(231, 76, 60, 0.26)" : "#F4CACA",
        label: "Negative",
      };
    }
  })();

  const renderChip = (label, value, config) => (
    <div
      className="rounded-pill px-2 py-1"
      style={{
        fontSize: "0.7rem",
        lineHeight: 1.2,
        color: config.color,
        background: config.bg,
        border: `1px solid ${config.border}`,
        fontWeight: 600,
      }}
      title={label}
    >
      {label}: {value}
    </div>
  );

  return (
    <div
      className="border rounded-4 px-3 py-2 mb-2"
      onClick={onClick}
      style={{
        cursor: "pointer",
        backgroundColor: theme.bg,
        borderColor: theme.border,
        color: theme.text,
        transition:
          "background-color 0.15s ease, border-color 0.15s ease, transform 0.12s ease, box-shadow 0.15s ease",
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.03)",
      }}
      onTouchStart={(e) => (e.currentTarget.style.transform = "scale(0.99)")}
      onTouchEnd={(e) => (e.currentTarget.style.transform = "scale(1)")}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = theme.hoverBg;
        e.currentTarget.style.borderColor = theme.hoverBorder;
        e.currentTarget.style.boxShadow = "0 6px 18px rgba(15, 23, 42, 0.06)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = theme.bg;
        e.currentTarget.style.borderColor = theme.border;
        e.currentTarget.style.boxShadow = "0 1px 2px rgba(15, 23, 42, 0.03)";
      }}
    >
      <div className="d-flex flex-column gap-2">
        <div
          className="d-flex align-items-start justify-content-between gap-3"
          style={{
            color: theme.text,
            fontSize: "0.94rem",
            fontWeight: 700,
            lineHeight: 1.3,
            letterSpacing: "-0.012em",
          }}
        >
          <div
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              flex: 1,
            }}
          >
            {title}
          </div>

          <a
            href={news.public_data_source_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: theme.muted,
              textDecoration: "none",
              fontSize: "0.82rem",
              flexShrink: 0,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "24px",
              height: "24px",
              marginTop: "2px",
            }}
            onClick={(e) => e.stopPropagation()}
            title="Open source"
            aria-label="Open source"
          >
            <i className="fa-solid fa-arrow-up-right-from-square" />
          </a>
        </div>

        <div
          className="d-flex align-items-center gap-2 flex-wrap"
          style={{ fontSize: "0.74rem", color: theme.muted }}
        >
          <div style={{ fontWeight: 700, color: theme.text }}>{displayDate}</div>
          {news.ner_relevance_score !== null &&
            news.ner_relevance_score !== undefined &&
            renderChip(
              "Rel",
              Number(news.ner_relevance_score).toFixed(2),
              relevanceIndicator
            )}

          {news.sentiment_score !== null &&
            news.sentiment_score !== undefined &&
            renderChip(
              sentimentIndicator.label,
              Number(news.sentiment_score).toFixed(2),
              sentimentIndicator
            )}
        </div>

        {previewText && (
          <div
            style={{
              color: theme.muted,
              fontSize: "0.8rem",
              lineHeight: 1.4,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {previewText}
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsItem;
